import {useEffect, useState} from "react";
import {useParams, Link} from "react-router-dom";
import {getFromApi, postToApi, patchToApi, putToApi, deleteFromApi} from "../../api/auth";
import ConfirmButton from "../../components/shared/ConfirmButton";

function EditableItem({item, onSave, onComplete, onDelete}) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft]     = useState(item.name);
    const [dueDraft, setDueDraft] = useState(item.due || "");

    const save = async () => {
        if (draft.trim()) await onSave(item._id, draft.trim(), dueDraft);
        setEditing(false);
    };

    if (editing) return (
        <div className="panel mb-2" style={{padding:"0.75rem 1rem"}}>
            <div className="d-flex gap-2 mb-2">
                <input className="form-control form-control-sm flex-grow-1" value={draft} onChange={e => setDraft(e.target.value)} autoFocus/>
                <input type="date" className="form-control form-control-sm" style={{maxWidth:150}} value={dueDraft} onChange={e => setDueDraft(e.target.value)}/>
            </div>
            <div className="d-flex gap-2">
                <button className="btn btn-sm btn-dark" onClick={save}>Save</button>
                <button className="btn btn-sm btn-outline-secondary" onClick={() => setEditing(false)}>Cancel</button>
            </div>
        </div>
    );

    return (
        <div className="panel d-flex align-items-center justify-content-between mb-2" style={{padding:"0.75rem 1rem"}}>
            <div>
                <div className="fw-medium">{item.name}</div>
                {item.due && <small className="text-muted">{item.due}</small>}
            </div>
            <div className="d-flex gap-1">
                <button className="btn btn-sm btn-outline-secondary" onClick={() => setEditing(true)} title="Edit">
                    <i className="bi bi-pencil"/>
                </button>
                <button className="btn btn-sm btn-success" onClick={() => onComplete(item._id)} title="Mark done">
                    <i className="bi bi-check-lg"/>
                </button>
                <ConfirmButton className="btn btn-sm btn-outline-danger" onConfirm={() => onDelete(item._id)}>
                    <i className="bi bi-trash"/>
                </ConfirmButton>
            </div>
        </div>
    );
}

export default function TodoView() {
    const {id} = useParams();
    const [group, setGroup]     = useState(null);
    const [itemName, setItemName] = useState("");
    const [due, setDue]         = useState("");
    const [search, setSearch]   = useState("");
    const [loading, setLoading] = useState(true);

    const load = async () => {
        try {
            const res = await getFromApi(`api/todo/${id}`);
            setGroup(res.data?.[0] || null);
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, [id]);

    const handleAdd = async (e) => {
        e.preventDefault();
        await postToApi(`api/todo/${id}/items`, {name: itemName, due});
        setItemName(""); setDue(""); load();
    };

    const handleEdit = async (itemId, name, due) => {
        await patchToApi(`api/todo/${id}/items/${itemId}`, {name, due});
        load();
    };

    const handleComplete = async (itemId) => {
        await putToApi(`api/todo/${id}/items/${itemId}`, {});
        load();
    };

    const handleDelete = async (itemId) => {
        await deleteFromApi(`api/todo/${id}/items/${itemId}`);
        load();
    };

    const handleClearDone = async () => {
        await deleteFromApi(`api/todo/${id}/items/done`);
        load();
    };

    if (loading) return <div className="text-muted py-4 text-center">Loading...</div>;
    if (!group)  return <div className="py-4">List not found.</div>;

    const filterItems = (items) => search
        ? (items || []).filter(i => i.name.toLowerCase().includes(search.toLowerCase()))
        : (items || []);

    return (
        <div>
            <div className="breadcrumb-nav mb-1"><Link to="/todo">Todo</Link> / {group.name}</div>
            <div className="page-header"><h4>{group.name}</h4></div>

            <div className="mb-3">
                <input className="form-control" placeholder="Filter tasks…" value={search} onChange={e => setSearch(e.target.value)}/>
            </div>

            <div className="panel mb-4">
                <form onSubmit={handleAdd} className="row g-2 align-items-end">
                    <div className="col-md-6">
                        <label className="form-label small fw-medium">Task</label>
                        <input className="form-control" placeholder="New task..." value={itemName} onChange={e => setItemName(e.target.value)} required/>
                    </div>
                    <div className="col-md-3">
                        <label className="form-label small fw-medium">Due date</label>
                        <input type="date" className="form-control" value={due} onChange={e => setDue(e.target.value)}/>
                    </div>
                    <div className="col-md-3">
                        <button type="submit" className="btn btn-dark w-100"><i className="bi bi-plus-lg me-1"/>Add</button>
                    </div>
                </form>
            </div>

            <div className="row g-4">
                <div className="col-md-6">
                    <div className="d-flex align-items-center gap-2 mb-3">
                        <span className="fw-semibold">Ongoing</span>
                        <span className="badge text-bg-warning text-dark">{group.uncompleted?.length || 0}</span>
                    </div>
                    {filterItems(group.uncompleted).map(item => (
                        <EditableItem key={item._id} item={item} onSave={handleEdit} onComplete={handleComplete} onDelete={handleDelete}/>
                    ))}
                    {!filterItems(group.uncompleted).length && <p className="text-muted small">{search ? "No matches." : "All done!"}</p>}
                </div>

                <div className="col-md-6">
                    <div className="d-flex align-items-center gap-2 mb-3">
                        <span className="fw-semibold">Done</span>
                        <span className="badge text-bg-success">{group.completed?.length || 0}</span>
                        {group.completed?.length > 0 && (
                            <ConfirmButton className="btn btn-sm btn-outline-secondary ms-auto" onConfirm={handleClearDone} confirmLabel="Clear all?">
                                <i className="bi bi-trash me-1"/>Clear done
                            </ConfirmButton>
                        )}
                    </div>
                    {filterItems(group.completed).map(item => (
                        <div key={item._id} className="panel d-flex align-items-center justify-content-between mb-2" style={{padding:"0.75rem 1rem", opacity:0.65}}>
                            <span className="text-decoration-line-through text-muted">{item.name}</span>
                            <ConfirmButton className="btn btn-sm btn-outline-danger" onConfirm={() => handleDelete(item._id)}>
                                <i className="bi bi-trash"/>
                            </ConfirmButton>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
